import * as React from 'react'

import { cn } from '#/shared/lib/utils'
import { ArrowUpDownIcon, EditIcon } from '#/shared/ui/icons'

import { HistoryRow } from './history-row'
import type { HistoryRowProps } from './history-row'

export interface NoteRowProps extends Omit<HistoryRowProps, 'icon'> {
  icon?: HistoryRowProps['icon']
  /** Drop the leading icon + divider entirely (e.g. the grouped notes list). */
  hideIcon?: boolean
  expandable?: boolean
  expanded?: boolean
  defaultExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
}

const COLLAPSED_HEIGHT_PX = 20

function ExpandToggle({
  expanded,
  onToggle,
}: {
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      aria-label={expanded ? 'Collapse note' : 'Expand note'}
      aria-expanded={expanded}
      onClick={onToggle}
      className="flex size-5 cursor-pointer items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-900"
    >
      <ArrowUpDownIcon
        className={cn(
          'size-4 transition-transform duration-200',
          expanded && 'rotate-180',
        )}
      />
    </button>
  )
}

function useContentOverflow() {
  const ref = React.useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = React.useState(0)

  React.useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const measure = () => setContentHeight(node.scrollHeight)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(node)
    return () => ro.disconnect()
  }, [])

  return {
    ref,
    contentHeight,
    overflows: contentHeight > COLLAPSED_HEIGHT_PX,
  }
}

function AnimatedNoteBody({
  expanded,
  contentHeight,
  contentRef,
  children,
}: {
  expanded: boolean
  contentHeight: number
  contentRef: React.RefObject<HTMLDivElement | null>
  children: React.ReactNode
}) {
  // Ellipsis-truncate the collapsed line, but only once the collapse
  // animation has settled — clamping earlier would snap the text to one
  // line mid-animation. scrollHeight still reports the full content height
  // on a clamped element, so the overflow measurement keeps working.
  const [clamped, setClamped] = React.useState(!expanded)
  React.useEffect(() => {
    if (expanded) setClamped(false)
  }, [expanded])
  return (
    <div
      style={{
        maxHeight: expanded
          ? contentHeight || COLLAPSED_HEIGHT_PX
          : COLLAPSED_HEIGHT_PX,
      }}
      onTransitionEnd={(e) => {
        if (e.target === e.currentTarget && !expanded) setClamped(true)
      }}
      className="overflow-hidden transition-[max-height] duration-300 ease-out"
    >
      <div ref={contentRef} className={cn(clamped && 'line-clamp-1')}>
        {children}
      </div>
    </div>
  )
}

/**
 * Returns the offset that centers the leading icon on the *collapsed* row.
 *
 * offset = (body height − title-row height) / 2, i.e. half of everything below
 * the title (meta + gaps + padding). That difference is invariant while the
 * title animates — the body and title row grow in lockstep — so the offset is
 * constant throughout expand/collapse and the icon never moves.
 */
function useCollapsedIconOffset() {
  const bodyRef = React.useRef<HTMLDivElement>(null)
  const titleRowRef = React.useRef<HTMLDivElement>(null)
  const [offset, setOffset] = React.useState(0)

  React.useLayoutEffect(() => {
    const body = bodyRef.current
    const titleRow = titleRowRef.current
    if (!body || !titleRow) return
    const measure = () =>
      setOffset(Math.max(0, (body.offsetHeight - titleRow.offsetHeight) / 2))
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(body)
    ro.observe(titleRow)
    return () => ro.disconnect()
  }, [])

  return { bodyRef, titleRowRef, offset }
}

function useExpandedState(
  controlled: boolean | undefined,
  defaultExpanded: boolean,
  onChange?: (expanded: boolean) => void,
) {
  const [uncontrolled, setUncontrolled] = React.useState(defaultExpanded)
  const isControlled = controlled !== undefined
  const expanded = isControlled ? controlled : uncontrolled
  const toggle = () => {
    const next = !expanded
    if (!isControlled) setUncontrolled(next)
    onChange?.(next)
  }
  return { expanded, toggle }
}

function mergeAction(
  action: React.ReactNode,
  expandButton: React.ReactNode,
): React.ReactNode {
  if (!action && !expandButton) return undefined
  return (
    <>
      {action}
      {expandButton}
    </>
  )
}

export function NoteRow({
  icon = EditIcon,
  hideIcon = false,
  expandable = true,
  expanded: controlledExpanded,
  defaultExpanded = false,
  onExpandedChange,
  action,
  title,
  ...rest
}: NoteRowProps) {
  const { expanded, toggle } = useExpandedState(
    controlledExpanded,
    defaultExpanded,
    onExpandedChange,
  )
  const { ref: contentRef, contentHeight, overflows } = useContentOverflow()
  const { bodyRef, titleRowRef, offset: iconOffsetY } = useCollapsedIconOffset()

  const canExpand = expandable && overflows
  const expandButton = canExpand ? (
    <ExpandToggle expanded={expanded} onToggle={toggle} />
  ) : null

  const mergedAction = mergeAction(action, expandButton)

  const animatedTitle = expandable ? (
    <AnimatedNoteBody
      expanded={expanded}
      contentHeight={contentHeight}
      contentRef={contentRef}
    >
      {title}
    </AnimatedNoteBody>
  ) : (
    title
  )

  return (
    <HistoryRow
      icon={hideIcon ? undefined : icon}
      action={mergedAction}
      title={animatedTitle}
      iconOffsetY={iconOffsetY}
      bodyRef={bodyRef}
      titleRowRef={titleRowRef}
      wrapTitle={expandable || expanded}
      {...rest}
    />
  )
}
